import { useFormik } from 'formik';
import React, { useState } from 'react';
import { FiLock } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { Button } from '@design-system/components/atoms/Button';
import { FormField } from '@design-system/components/molecules/FormField';
import { authService } from '@features/authentication/services/auth.service';
import { APP_ROUTES } from '@shared/constants/routes.constants';

const createPasswordSchema = Yup.object({
  password: Yup.string()
    .min(8, 'A senha deve ter no mínimo 8 caracteres')
    .matches(/[A-Z]/, 'A senha deve conter pelo menos uma letra maiúscula')
    .matches(/[a-z]/, 'A senha deve conter pelo menos uma letra minúscula')
    .matches(/[0-9]/, 'A senha deve conter pelo menos um número')
    .required('Senha é obrigatória'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'As senhas devem ser iguais')
    .required('Confirmação de senha é obrigatória'),
});

export interface CreatePasswordFormProps {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * CreatePasswordForm component
 * Used for first-time access users to set their password
 * Requires user to be authenticated (has token from login)
 */
export const CreatePasswordForm: React.FC<CreatePasswordFormProps> = ({ onSuccess, onError }) => {
  const [serverError, setServerError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: createPasswordSchema,
    onSubmit: async (values) => {
      setServerError('');
      setIsLoading(true);

      try {
        // Call API to set password (first access)
        await authService.setPassword(values.password);

        onSuccess?.();
        // Redirect to modules page after successful password creation
        void navigate(APP_ROUTES.MODULES, { replace: true });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Erro ao criar senha. Tente novamente.';

        setServerError(errorMessage);
        onError?.(error instanceof Error ? error : new Error(errorMessage));
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
      <FormField
        name="password"
        label="Senha"
        type="password"
        placeholder="Digite sua nova senha"
        value={formik.values.password}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.errors.password}
        touched={formik.touched.password}
        required
        fullWidth
        autoComplete="new-password"
        icon={<FiLock className="w-5 h-5" />}
      />

      <FormField
        name="confirmPassword"
        label="Confirmar senha"
        type="password"
        placeholder="Digite sua senha novamente"
        value={formik.values.confirmPassword}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.errors.confirmPassword}
        touched={formik.touched.confirmPassword}
        required
        fullWidth
        autoComplete="new-password"
        icon={<FiLock className="w-5 h-5" />}
      />

      {serverError && (
        <div
          className="p-3 rounded-lg bg-danger-50 border border-danger-200 text-danger-700 text-sm"
          role="alert"
        >
          {serverError}
        </div>
      )}

      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        loading={isLoading}
        disabled={isLoading || !formik.isValid}
      >
        {isLoading ? 'Criando...' : 'Criar senha definitiva'}
      </Button>
    </form>
  );
};
